import React, { useEffect, useMemo, useState } from "react";
import SectionField from "../section/SectionField";
import styles from "./RatingComponent.module.scss";
import InputTextArea from "components/shared/textArea/InputTextArea";
import FileUploadButton from "components/shared/fileUploadButton/FileUploadButton";
import { Controller, useForm } from "react-hook-form";
import TriggerButton from "components/shared/createTriggerButton/TriggerButton";
import { useDispatch, useSelector } from "react-redux";
import { setDroppedItems, setUpdateDroppedItems } from "@redux/slices/formSlice";
import { handleFile, randomIntFromInterval } from "utils/utils";
import DeleteIcon from "@mui/icons-material/Delete";
import { setPublish } from "@redux/slices/globalSlice";
import { setDroppedItems as setTriggerList } from "@redux/slices/triggerSlice";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";

const RatingComponent = () => {
  const { publish } = useSelector((state) => state?.global);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { triggerDroppedList } = useSelector((state) => state.trigger);
  const { droppedItems, selectedData } = useSelector((state) => state.form);
  const { formId } = useParams();
  const [file, setFile] = useState("");
  const [minValue, setMinValue] = useState(1);
  const [maxValue, setMaxValue] = useState(10);
  const [isError, setIsError] = useState(false);

  const defaultValues = useMemo(() => ({
    showInstructions: false,
    allowNA: false,
    answers: [
      { label: "Yes", evaluation: "" },
      { label: "No", evaluation: "" },
    ],
    instructions: selectedData?.instructions || "",
    enableDependentQuestions: false,
    options: [minValue, maxValue],  // Now updates when state changes
  }), [minValue, maxValue, selectedData]);


  const { register, control, watch, setValue, formState: { isDirty }, reset } = useForm({
    defaultValues,
  });

  const currentItem = useMemo(() => droppedItems.find((item) => item.id === selectedData.id), [
    droppedItems,
    selectedData,
  ]);




  const handleRangeChange = (type, value) => {
    const numericValue = parseInt(value, 10);

    if (type === "min") {
      setMinValue((prevMin) => {
        if (numericValue > maxValue || numericValue < 1) {
          setIsError(true);
          return prevMin; // Keep the previous state if invalid
        } else {
          setIsError(false);
          setValue("options", [numericValue || 1, maxValue || 10])
          return numericValue;
        }
      });
    } else if (type === "max") {
      setMaxValue((prevMax) => {
        if (numericValue < minValue) {
          setIsError(true);
          return prevMax; // Keep the previous state if invalid
        } else {
          setIsError(false);
          setValue("options", [minValue || 1, numericValue || 10])
          return numericValue;
        }
      });
    }
  };


  const handleFileChange = (file, setIsLoading) => {
    setFile(file);
    handleFile(file, droppedItems, selectedData, dispatch, setDroppedItems, setIsLoading);;
    dispatch(setPublish(true));
  };

  const handleImageDelete = (src, selectedItem) => {
    const updatedMedia = selectedItem?.data?.media.filter((media) => media.path !== src);
    dispatch(setUpdateDroppedItems({ id: selectedData.id, updatedData: { media: updatedMedia } }));
    setFile("");
    dispatch(setPublish(true));
  };

  const handleOpenImage = (src) => {
    window.open(src, "__blank");
  };

  const handleTrigger = () => {
    const uniqueId = Date.now() + randomIntFromInterval(1000, 9999);
    dispatch(setTriggerList([...triggerDroppedList, { ...currentItem, id: uniqueId }]));
    navigate(`/create/triggers/${formId || ""}`);
  };

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    if (selectedData?.options) {
      setMinValue(Number(selectedData.options[0]) || 1);
      setMaxValue(Number(selectedData.options[1]) || 10);
    }
  }, [selectedData]);

  useEffect(() => {
    if (!isDirty) {
      const data = structuredClone(watch())
      dispatch(setUpdateDroppedItems({ id: selectedData.id, updatedData: data }));
    }

    const subscription = watch((data) => {
      const formData = structuredClone(data)
      dispatch(setUpdateDroppedItems({ id: selectedData.id, updatedData: formData }));
      dispatch(setPublish(true))
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  return (
    <>
      <SectionField placeholder="Please enter your question here." message="Required" />
      <div className={styles.instructionsForm}>
        <h4>Instructions</h4>
        <div className={styles.formField}>
          <Controller
            name="instructions"
            control={control}
            render={({ field }) => (
              <InputTextArea
                {...field}
                control={control}
                type="textarea"
                placeholder="Optional"
                onChange={(e) => {
                  field.onChange(e);
                  if(!e.target.value) setValue("showInstructions", false);
                }}
                className={styles.textArea}
              />
            )}
          />
        </div>


        <div className={styles.formField}>
          {currentItem?.data?.media?.length ? (
            <div className={styles.imageContainer}>
              {currentItem?.data?.media?.map((src, index) => (
                <div key={index} className={styles.imageWrapper} onClick={() => handleOpenImage(src.path)}>
                  <img src={src.path} alt="" />
                  <div
                    className={styles.cancelIcon}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      handleImageDelete(src.path, currentItem);
                    }}
                  >
                    <DeleteIcon sx={{ width: "20px", height: "20px" }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <FileUploadButton name="mediaFile" control={control} onFileChange={handleFileChange} />
          )}
        </div>

        <div className={styles.rangeContainer}>
          <div>
            <div className={styles.rangeField}>
              <label>Min Value</label>
              <div className={styles.rangeInputWrapper}>
                <input
                  type="number"
                  value={minValue}
                  onChange={(e) => handleRangeChange("min", e.target.value)}
                  min={1}
                  max={maxValue}
                  className={styles.rangeInput}
                />
              </div>
            </div>
            <div className={styles.rangeField}>
              <label>Max Value</label>
              <div className={styles.rangeInputWrapper}>
                <input
                  type="number"
                  value={maxValue}
                  onChange={(e) => handleRangeChange("max", e.target.value)}
                  min={minValue}
                  className={styles.rangeInput}
                />
              </div>
            </div>
          </div>
          {isError && <span style={{ color: "red", fontSize: "13px" }}>Please select a valid range</span>}
        </div>


      </div>

      <form className={styles.formContainer}>
        <div>
          <label>
            <input
              type="checkbox"
              disabled={!watch("instructions") && !file && !(currentItem?.data?.media?.length > 0)}
              {...register("showInstructions")}
              />
            Show instructions by default
          </label>
        </div>
        <TriggerButton handleClick={() => {
          if (!publish) {
            handleTrigger()
          } else {
            toast.error("There are unpublished changes");
          }
        }

        } />
      </form>
    </>
  );
};

export default RatingComponent;
